/**
 * FoodPulseCitiesHub — index of major US city food-directory pages.
 *
 * Route: /food-pulse/city
 * Backend: GET /api/city-directory/hub  (counts fetched lazily; the
 * curated list itself is defined below so first paint is instant).
 *
 * Each card deep-links to /food-pulse/city/US/{state}/{city-slug}.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Search, MapPin, ArrowRight } from "lucide-react";
import SeoHead from "@/components/SeoHead";

const API = process.env.REACT_APP_BACKEND_URL;

// Curated set of major US food cities. Every entry has a live seed of
// businesses in `food_businesses` (verified against Mongo counts).
// Sorted roughly by market size then grouped so state-batches sit
// together for browsability.
const US_CITIES = [
  // Batch 1 — Top-16 metros
  { city: "New York",       state: "NY", hint: "5,800+ restaurants across five boroughs" },
  { city: "Brooklyn",       state: "NY", hint: "Farm-to-table, pizza, deli mainstays" },
  { city: "Houston",        state: "TX", hint: "Tex-Mex, Vietnamese, BBQ, food trucks" },
  { city: "Chicago",        state: "IL", hint: "Deep-dish, steakhouses & Devon Ave. eats" },
  { city: "Los Angeles",    state: "CA", hint: "Tacos, K-town, sushi, farmers markets" },
  { city: "Philadelphia",   state: "PA", hint: "Cheesesteaks, Reading Terminal, BYOBs" },
  { city: "Miami",          state: "FL", hint: "Cuban, Caribbean, seafood, cafecito" },
  { city: "San Francisco",  state: "CA", hint: "Mission burritos, dim sum, coffee roasters" },
  { city: "Dallas",         state: "TX", hint: "Steakhouses, Tex-Mex, Korean BBQ" },
  { city: "Portland",       state: "OR", hint: "Food carts, coffee, farm-driven kitchens" },
  { city: "Atlanta",        state: "GA", hint: "Southern soul food, Buford Highway global" },
  { city: "Seattle",        state: "WA", hint: "Coffee, Pike Place, PNW seafood" },
  { city: "Denver",         state: "CO", hint: "Green chile, breweries, mountain cafes" },
  { city: "Phoenix",        state: "AZ", hint: "Sonoran, Mexican markets, food halls" },
  { city: "Washington",     state: "DC", hint: "Ethiopian, half-smoke, chef-driven" },
  { city: "Boston",         state: "MA", hint: "Seafood, North End Italian, chowder" },
  // Batch 2 — WA, CA, NV, ID, CO, NJ, FL, GA expansion (seeding live)
  { city: "Spokane",         state: "WA", hint: "Inland NW farms, cafes, craft beer" },
  { city: "Tacoma",          state: "WA", hint: "Waterfront eats, breweries, wine bars" },
  { city: "San Diego",       state: "CA", hint: "Fish tacos, craft beer, border eats" },
  { city: "Sacramento",      state: "CA", hint: "Farm-to-fork capital, valley produce" },
  { city: "San Jose",        state: "CA", hint: "South Bay Vietnamese, tech-cafeteria hits" },
  { city: "Oakland",         state: "CA", hint: "Diverse Bay food halls, temescal cafes" },
  { city: "Las Vegas",       state: "NV", hint: "Strip steakhouses, celebrity chef row" },
  { city: "Reno",            state: "NV", hint: "Basque, casinos, Truckee River bites" },
  { city: "Boise",           state: "ID", hint: "Basque block, brew pubs, farm markets" },
  { city: "Boulder",         state: "CO", hint: "Pearl St. bistros, farm-to-fork pioneers" },
  { city: "Colorado Springs",state: "CO", hint: "Front-range diners, breweries, tacos" },
  { city: "Newark",          state: "NJ", hint: "Ironbound Portuguese, Brazilian classics" },
  { city: "Jersey City",     state: "NJ", hint: "Journal Square globals, waterfront cafes" },
  { city: "Hoboken",         state: "NJ", hint: "Little Italy classics, Washington St. bars" },
  { city: "Orlando",         state: "FL", hint: "Latin bakeries, theme-park chef restaurants" },
  { city: "Tampa",           state: "FL", hint: "Cuban sandwiches, Ybor City heritage" },
  { city: "Jacksonville",    state: "FL", hint: "Shrimp shacks, southern BBQ, riverwalk bites" },
  { city: "St. Petersburg",  state: "FL", hint: "Gulf seafood, craft beer, indie bakeries" },
  { city: "Savannah",        state: "GA", hint: "Lowcountry classics, riverfront kitchens" },
  { city: "Athens",          state: "GA", hint: "College-town eats, farm suppers, taquerias" },
  { city: "Augusta",         state: "GA", hint: "Southern staples, riverwalk bistros" },
];

function CityCard({ city, state, hint, count }) {
  const slug = city.replace(/\s+/g, "-");
  const to = `/food-pulse/city/US/${state}/${slug}`;
  return (
    <Link
      to={to}
      className="rounded-xl border p-5 flex flex-col gap-2 bg-white hover:shadow-md hover:-translate-y-0.5 transition-all"
      style={{ borderColor: "#EADFCB" }}
      data-testid={`hub-city-${slug.toLowerCase()}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MapPin size={16} style={{ color: "#B25D38" }}/>
          <span className="font-semibold text-lg" style={{ color: "#3C2A17" }}>
            {city}
          </span>
          <span className="text-xs uppercase tracking-wider" style={{ color: "#8B5E10" }}>
            {state}
          </span>
        </div>
        <ArrowRight size={14} style={{ color: "#8B5E10" }}/>
      </div>
      <p className="text-xs" style={{ color: "#5A4A32" }}>{hint}</p>
      {typeof count === "number" && count > 0 && (
        <p className="text-[11px] font-mono mt-1" style={{ color: "#8B5E10" }}>
          {count.toLocaleString()} listings
        </p>
      )}
    </Link>
  );
}

export default function FoodPulseCitiesHub() {
  const [counts, setCounts] = useState({});
  const [q, setQ] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/city-directory/hub`);
        if (!cancelled) setCounts(res.data?.counts || {});
      } catch (_) { /* hub counts are optional */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return US_CITIES;
    return US_CITIES.filter((c) =>
      c.city.toLowerCase().includes(needle) ||
      c.state.toLowerCase().includes(needle));
  }, [q]);

  return (
    <div className="min-h-screen" style={{ background: "#FDFBF7" }}>
      <SeoHead
        title="US Food City Directories — Food Pulse"
        description="Explore every US city's food scene. Restaurants, cafes, bakeries, food trucks, markets and farms — searchable, deduplicated, and owner-claimable."
      />

      <div className="max-w-6xl mx-auto px-4 pt-8 pb-4">
        <div className="hidden sm:flex items-center gap-3 mb-6">
          <img
            src="/foodpulse/fp-logo-round.jpg"
            alt="Food Pulse"
            className="w-12 h-12 rounded-full ring-2 ring-white shadow"
            data-testid="hub-fp-logo"
          />
          <div>
            <div className="text-lg font-bold leading-none" style={{ color: "#3C2A17" }}>
              Food Pulse<sup className="text-[9px] ml-0.5" style={{ color: "#8B5E10" }}></sup>
            </div>
            <div className="text-[10px] italic tracking-wide" style={{ color: "#8B5E10" }}>
              Social Food Discovery App
            </div>
          </div>
        </div>
        <p className="text-xs uppercase tracking-[0.2em]" style={{ color: "#8B5E10" }}>
          Food Pulse · US City Directories
        </p>
        <h1 className="mt-2 text-2xl sm:text-4xl lg:text-5xl font-serif" style={{ color: "#3C2A17" }}>
          Every US city's food scene, one open directory.
        </h1>
        <p className="mt-3 text-sm max-w-2xl" style={{ color: "#5A4A32" }}>
          Restaurants, cafes, bakeries, food trucks, farmers markets, food banks
          and farms — all in one searchable, duplicate-free map. Owners can
          claim their listing in under a minute and start editing it live.
        </p>

        <div className="relative mt-6 max-w-lg">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2"
                  style={{ color: "#8B5E10" }}/>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search cities…"
            className="w-full pl-9 pr-4 py-3 rounded-xl border outline-none focus:ring-2"
            style={{ borderColor: "#EADFCB", background: "#fff" }}
            data-testid="hub-city-search"
          />
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 pb-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          {filtered.map((c) => (
            <CityCard
              key={`${c.city}-${c.state}`}
              city={c.city}
              state={c.state}
              hint={c.hint}
              count={counts[`${c.state}:${c.city}`]}
            />
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="py-16 text-center text-sm" style={{ color: "#5A4A32" }}>
            No matches yet. Your city not on the map?{" "}
            <a href="mailto:hello@foodpulse.net" className="underline">
              Request it
            </a>.
          </div>
        )}

        <div className="mt-14 border rounded-xl p-6 text-center"
             style={{ borderColor: "#EADFCB", background: "#FBEED6" }}>
          <h3 className="text-lg font-serif" style={{ color: "#3C2A17" }}>
            More cities on the way.
          </h3>
          <p className="text-sm mt-1" style={{ color: "#5A4A32" }}>
            We're expanding to every major US metro this quarter. Want yours
            prioritised?{" "}
            <a href="mailto:hello@foodpulse.net" className="underline">
              hello@foodpulse.net
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
